import { addMonths, differenceInCalendarMonths, format, parseISO } from 'date-fns';
import type { TOptions } from 'i18next';
import type { ContractReconciliationRow } from './sales-force-contract-reconciliation';

export type ContractText = (key: string, options?: TOptions) => string;
export const contractFilters = ['linkage', 'activity', 'renewal', 'fields', 'unresolved', 'warningsOnly'] as const;
export type ContractFilter = typeof contractFilters[number];
export const contractDate = (value: string | null) => value?.slice(0, 10) ?? '-';

export function renewalLag(row: ContractReconciliationRow): { system: 'SF' | 'WK'; periods: number } | null {
  if (row.salesforceContracts.length !== 1 || row.webkuaContracts.length !== 1 || row.issues.length > 0
    || row.sourceDiagnostics?.some((diagnostic) => diagnostic.issues.length > 0)) return null;
  const sf = row.salesforceContracts[0];
  const wk = row.webkuaContracts[0];
  const interval = sf.invoiceInterval;
  if (sf.isCurrent !== true || wk.isCurrent !== true || !sf.companyNumber || sf.companyNumber !== wk.companyNumber
    || !sf.nextRenewalDate || !wk.nextRenewalDate || interval == null || !Number.isInteger(interval) || interval <= 0
    || interval !== wk.invoiceInterval) return null;
  const sfDate = contractDate(sf.nextRenewalDate);
  const wkDate = contractDate(wk.nextRenewalDate);
  const months = Math.abs(differenceInCalendarMonths(parseISO(sfDate), parseISO(wkDate)));
  if (months === 0 || months % interval !== 0) return null;
  const earlier = sfDate < wkDate ? sfDate : wkDate;
  const later = sfDate < wkDate ? wkDate : sfDate;
  if (format(addMonths(parseISO(earlier), months), 'yyyy-MM-dd') !== later) return null;
  return { system: sfDate < wkDate ? 'SF' : 'WK', periods: months / interval };
}

export function contractPresence(row: ContractReconciliationRow, text: ContractText): string {
  const key = row.salesforceContracts.length > 0
    ? row.webkuaContracts.length > 0 ? 'both' : 'salesforceOnly'
    : row.webkuaContracts.length > 0 ? 'webkuaOnly' : 'neither';
  const multiple = row.salesforceContracts.length > 1 || row.webkuaContracts.length > 1;
  return [text(`presence.${key}`), ...(multiple ? [text('multipleMatches')] : [])].join(' · ');
}

export function contractValue(field: string, raw: string | null, text: ContractText): string {
  if (field === 'isCurrent') return text(raw === 'true' ? 'current' : raw === 'false' ? 'ended' : 'unknown');
  if (raw == null) return text(field === 'terminationDate' ? 'noTerminationDate' : 'notReported');
  return raw;
}

export function contractNextCheck(field: string, text: ContractText): string {
  const keys: Record<string, string> = {
    companyNumber: 'customerLink', isCurrent: 'activity', nextRenewalDate: 'renewal',
    startDate: 'dates', terminationDate: 'dates', currency: 'billing',
    invoicedCompanyNumber: 'billing', invoiceInterval: 'interval', contractResponsibleEmail: 'contact',
  };
  return text(`checks.${keys[field] ?? 'sourceData'}`);
}

export function contractIssueMessages(
  row: ContractReconciliationRow, text: ContractText, excludedIssues: readonly string[] = [],
): string[] {
  const diagnostics = row.sourceDiagnostics;
  const attributed = diagnostics?.flatMap((diagnostic) => diagnostic.issues
    .filter((issue) => !excludedIssues.includes(issue)).map((issue) => ({
    issue, label: text('sourceIssue', { system: diagnostic.system, issue: text(`issues.${issue}`) }),
  }))) ?? [];
  const unknownSide = row.issues.filter((issue) => !excludedIssues.includes(issue)
    && !attributed.some((entry) => entry.issue === issue));
  return [...new Set([...attributed.map((entry) => entry.label),
    ...unknownSide.map((issue) => text('unattributedIssue', { issue: text(`issues.${issue}`) }))])];
}

export function matchesContractFilter(row: ContractReconciliationRow, filter: ContractFilter): boolean {
  const hasField = (field: string) => row.differences.some((difference) => difference.field === field);
  const unresolved = row.issues.length > 0 || (row.sourceDiagnostics?.length ?? 0) > 0
    || row.kind === 'MissingKey' || row.salesforceContracts.length > 1 || row.webkuaContracts.length > 1;
  switch (filter) {
    case 'linkage': return row.kind === 'MissingKey' || row.salesforceContracts.length !== 1
      || row.webkuaContracts.length !== 1 || hasField('companyNumber');
    case 'activity': return hasField('isCurrent');
    case 'renewal': return hasField('nextRenewalDate')
      || row.warnings.some((warning) => ['SalesforceRenewalDatePassed', 'WebkuaRenewalDatePassed'].includes(warning));
    case 'fields': return row.differences.some((difference) =>
      !['companyNumber', 'isCurrent', 'nextRenewalDate'].includes(difference.field));
    case 'unresolved': return unresolved;
    case 'warningsOnly': return !unresolved && row.differences.length === 0 && row.warnings.length > 0
      && row.salesforceContracts.length === 1 && row.webkuaContracts.length === 1;
  }
}

export function contractRank(row: ContractReconciliationRow): number {
  const order: ContractFilter[] = ['linkage', 'unresolved', 'activity', 'renewal', 'fields', 'warningsOnly'];
  const index = order.findIndex((filter) => matchesContractFilter(row, filter));
  return index < 0 ? order.length : index;
}

export function contractFindings(row: ContractReconciliationRow, text: ContractText): string[] {
  const findings: string[] = [];
  if (row.kind === 'MissingKey') findings.push(text('missingLinkKey'));
  if (row.salesforceContracts.length > 1 || row.webkuaContracts.length > 1) {
    findings.push(text('ambiguousSources', { sf: row.salesforceContracts.length, wk: row.webkuaContracts.length }));
  }
  if (row.kind !== 'MissingKey') {
    if (row.salesforceContracts.length === 0) findings.push(text('notFoundInSalesforce'));
    if (row.webkuaContracts.length === 0) findings.push(text('notFoundInWebkua'));
  }
  findings.push(...contractIssueMessages(row, text, row.kind === 'MissingKey' ? ['MissingContractNumber'] : []));
  const issueFields: Record<string, string> = {
    MissingCompanyNumber: 'companyNumber', UnknownActivity: 'isCurrent', MissingStartDate: 'startDate',
    MissingNextRenewalDate: 'nextRenewalDate', InvalidInvoiceInterval: 'invoiceInterval',
    MissingCurrency: 'currency', MissingInvoicedCompanyNumber: 'invoicedCompanyNumber',
  };
  const invalidFields = new Set([...row.issues, ...(row.sourceDiagnostics?.flatMap((diagnostic) => diagnostic.issues) ?? [])]
    .map((issue) => issueFields[issue]).filter(Boolean));
  const priority: Record<string, number> = { companyNumber: 0, isCurrent: 1, nextRenewalDate: 2 };
  for (const difference of [...row.differences].sort((a, b) => (priority[a.field] ?? 3) - (priority[b.field] ?? 3))) {
    if (invalidFields.has(difference.field)) continue;
    if (difference.field === 'isCurrent') {
      const sfStatus = row.salesforceContracts.length === 1 ? row.salesforceContracts[0].status : null;
      const activity = contractValue('isCurrent', difference.salesforceValue, text);
      findings.push(text('activityFinding', {
        sf: sfStatus ? `${sfStatus} (${activity})` : activity,
        wk: contractValue('isCurrent', difference.webkuaValue, text),
      }));
    } else if (difference.field === 'companyNumber') {
      findings.push(text('customerFinding', {
        sf: difference.salesforceValue ?? text('notReported'), wk: difference.webkuaValue ?? text('notReported'),
      }));
    } else if (difference.field === 'nextRenewalDate') {
      const lag = renewalLag(row);
      findings.push(lag ? text(lag.system === 'WK' ? 'laggingWebkua' : 'laggingSalesforce', { count: lag.periods })
        : text('differentRenewalDates'));
    } else {
      findings.push(text('fieldFinding', { field: text(`fields.${difference.field}`) }));
    }
  }
  findings.push(...row.warnings.map((warning) => text(`warnings.${warning}`)));
  return [...new Set(findings)];
}
